import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Slide from '@material-ui/core/Slide';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import Divider from '@material-ui/core/Divider';
import { i18n } from '../../translate/i18n';
import { getQueueOptions } from '../../services/queueService';
import { getWhatsAppOptions } from '../../services/whatsappService';
import { getContactOptions, createContact } from '../../services/contactService';
import { createTicket, updateTicket } from '../../services/ticketService';
import ButtonWithSpinner from '../ButtonWithSpinner';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import {
  Box,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  Tooltip,
} from '@material-ui/core';
import { Facebook, Instagram, WhatsApp } from '@material-ui/icons';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@material-ui/icons/Close';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { formatPhoneBR } from '../../utils/phoneBR';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const filter = createFilterOptions({ trim: true });

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(15,23,42,0.14)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 620,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '95vw',
      margin: theme.spacing(1),
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e8edf2',
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#e8f0fe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 22, color: '#1a73e8' },
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: '#64748b',
    marginTop: 1,
  },
  closeButton: {
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    '&:hover': { backgroundColor: '#f1f5f9', color: '#334155' },
  },
  dialogContent: {
    padding: '20px',
    backgroundColor: '#f8fafc',
  },
  dialogActions: {
    padding: '12px 20px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e8edf2',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  field: {
    backgroundColor: '#fff',
    borderRadius: 8,
    '& .MuiOutlinedInput-root': { borderRadius: 8 },
  },
  btn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 36,
  },
  online: {
    fontSize: 11,
    color: '#25d366',
  },
  offline: {
    fontSize: 11,
    color: '#e1306c',
  },
  newContactBox: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
    borderRadius: 6,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  newContactLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#1565c0',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 4,
  },
}));

const isNumeric = (str) => /^\d+$/.test(str.replace(/[\s\-().+]/g, ''));

const getContactSearchLabel = (contact) => {
  if (!contact) return '';
  if (contact.number) {
    return formatPhoneBR(contact.number) || contact.number;
  }
  return contact.name || '';
};

const NewTicketModal = ({ modalOpen, onClose, initialContact, confirmLabel }) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState('');
  const [selectedWhatsapp, setSelectedWhatsapp] = useState('');
  const [queueError, setQueueError] = useState('');
  const [whatsapps, setWhatsapps] = useState([]);
  const [queues, setQueues] = useState([]);

  // Estado para criação de novo contato inline
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [newNameError, setNewNameError] = useState('');
  const [newNumberError, setNewNumberError] = useState('');

  const { user } = useContext(AuthContext);
  const { whatsappId } = user;

  const resetState = () => {
    setSearchParam('');
    setSelectedContact(null);
    setIsCreating(false);
    setNewName('');
    setNewNumber('');
    setNewNameError('');
    setNewNumberError('');
    setSelectedQueue('');
    setQueueError('');
  };

  // Pre-fill contact when opened from lista de contatos / CRM
  useEffect(() => {
    if (!modalOpen) return;
    if (!initialContact?.id && !initialContact?.number) return;
    setSelectedContact(initialContact.id ? initialContact : null);
    if (initialContact.id) {
      setOptions([initialContact]);
    }
    setSearchParam(getContactSearchLabel(initialContact));
  }, [modalOpen, initialContact]);

  useEffect(() => {
    if (!modalOpen) return;

    const fetchQueues = async () => {
      try {
        const { data } = await getQueueOptions();
        const allQueues = Array.isArray(data) ? data : [];
        const userQueueIdSet = new Set((user?.queues || []).map((q) => String(q.id)));
        const canViewAllQueues =
          user?.super || user?.isOwner || user?.permissions?.includes('tickets:read_all');
        let filtered =
          canViewAllQueues || userQueueIdSet.size === 0
            ? allQueues
            : allQueues.filter((q) => userQueueIdSet.has(String(q.id)));
        if (userQueueIdSet.size > 0 && filtered.length === 0) filtered = allQueues;
        setQueues(filtered);
        if (filtered.length === 1) setSelectedQueue(filtered[0].id);
      } catch (err) {
        setQueues([]);
        toastError(err);
      }
    };

    fetchQueues();
  }, [modalOpen, user?.profile, user?.queues]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const timer = setTimeout(async () => {
      try {
        const { data } = await getWhatsAppOptions();
        setWhatsapps(data);
      } catch {
        /* noop */
      }
    }, 300);
    if (whatsappId != null) setSelectedWhatsapp(whatsappId);
    return () => clearTimeout(timer);
  }, [modalOpen]);

  // Busca contatos por nome ou número
  useEffect(() => {
    if (!modalOpen || searchParam.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await getContactOptions({ searchParam });
        setOptions(data.contacts || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchParam, modalOpen]);

  const IconChannel = (channel) => {
    switch (channel) {
      case 'facebook':
        return <Facebook style={{ color: '#3b5998', verticalAlign: 'middle' }} />;
      case 'instagram':
        return <Instagram style={{ color: '#e1306c', verticalAlign: 'middle' }} />;
      case 'whatsapp':
        return <WhatsApp style={{ color: '#25d366', verticalAlign: 'middle' }} />;
      default:
        return null;
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSelectOption = (e, value) => {
    if (!value) {
      setSelectedContact(null);
      setIsCreating(false);
      return;
    }
    if (value._create) {
      // Usuário escolheu criar novo contato
      setIsCreating(true);
      setSelectedContact(null);
      // Pré-preenche número ou nome conforme o que foi digitado
      if (isNumeric(searchParam)) {
        setNewNumber(searchParam.replace(/\D/g, ''));
        setNewName('');
      } else {
        setNewName(searchParam);
        setNewNumber('');
      }
    } else {
      setSelectedContact(value);
      setIsCreating(false);
    }
  };

  const normalize = (n) => n.replace(/\D/g, '');

  const handleSaveTicket = async () => {
    if (selectedQueue === '') {
      setQueueError('Selecione uma fila');
      return;
    }
    setQueueError('');

    setLoading(true);
    try {
      let contactId = null;

      if (isCreating) {
        if (!newName.trim()) {
          setNewNameError('Informe o nome');
          setLoading(false);
          return;
        }
        if (!newNumber.trim() || normalize(newNumber).length < 8) {
          setNewNumberError('Informe um número válido com DDI (ex: 5511999999999)');
          setLoading(false);
          return;
        }
        setNewNameError('');
        setNewNumberError('');
        const { data: created } = await createContact({
          name: newName.trim(),
          number: normalize(newNumber),
          email: '',
        });
        contactId = created.id;
      } else if (selectedContact) {
        contactId = selectedContact.id;
      } else {
        toastError({ message: 'Selecione ou crie um contato' });
        setLoading(false);
        return;
      }

      const queueId = selectedQueue || null;
      const wppId = selectedWhatsapp || null;

      try {
        const { data: ticket } = await createTicket({
          contactId,
          queueId,
          whatsappId: wppId,
          userId: user.id,
          status: 'open',
        });
        onClose(ticket);
      } catch (err) {
        const payload = err?.response?.data?.error;
        let ticket = null;
        if (typeof payload === 'string') {
          try {
            ticket = JSON.parse(payload);
          } catch {
            /* noop */
          }
        } else if (typeof payload === 'object' && payload !== null) {
          ticket = payload;
        }

        if (ticket?.id) {
          try {
            const { data: updated } = await updateTicket(ticket.id, {
              status: 'open',
              userId: user.id,
            });
            onClose(updated);
          } catch {
            onClose(ticket);
          }
          return;
        }
        toastError(err);
      }
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const createFilterOptions_ = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue.length >= 2 && !loading) {
      filtered.push({ _create: true, name: `Criar contato "${params.inputValue}"` });
    }
    return filtered;
  };

  const canConfirm = (selectedContact || isCreating) && !loading;

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.paper }}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIconBox}>
            <WhatsApp />
          </Box>
          <Box>
            <Typography className={classes.headerTitle}>Nova conversa</Typography>
            <Typography className={classes.headerSubtitle}>
              Busque um contato pelo nome ou número, ou crie um novo
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Fechar">
          <IconButton className={classes.closeButton} size="small" onClick={handleClose}>
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <DialogContent dividers className={classes.dialogContent}>
        <Grid container spacing={2}>
          {/* Busca por nome ou número */}
          <Grid xs={12} item>
            <Autocomplete
              fullWidth
              options={options}
              loading={loading}
              clearOnBlur={false}
              autoHighlight
              freeSolo
              clearOnEscape
              value={isCreating ? null : selectedContact}
              inputValue={searchParam}
              onInputChange={(_e, newValue, reason) => {
                setSearchParam(newValue);
                if (reason === 'input') {
                  setSelectedContact(null);
                  setIsCreating(false);
                }
              }}
              getOptionLabel={(option) =>
                option._create ? option.name : `${option.name} - ${option.number}`
              }
              filterOptions={createFilterOptions_}
              onChange={handleSelectOption}
              renderOption={(option) =>
                option._create ? (
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1565c0' }}>
                    <PersonAddIcon fontSize="small" />
                    <span>{option.name}</span>
                  </Box>
                ) : (
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {IconChannel(option.channel)}
                    <Typography style={{ fontSize: 14 }}>
                      {option.name} — {option.number}
                    </Typography>
                  </Box>
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar por nome ou número"
                  variant="outlined"
                  autoFocus
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress color="inherit" size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Formulário inline para novo contato */}
          {isCreating && (
            <Grid xs={12} item>
              <Box className={classes.newContactBox}>
                <Box className={classes.newContactLabel}>
                  <PersonAddIcon fontSize="small" />
                  Novo contato
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Nome *"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setNewNameError('');
                  }}
                  error={Boolean(newNameError)}
                  helperText={newNameError}
                  autoFocus
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Número (DDI + DDD + número)"
                  placeholder="5511999999999"
                  value={newNumber}
                  onChange={(e) => {
                    setNewNumber(e.target.value);
                    setNewNumberError('');
                  }}
                  error={Boolean(newNumberError)}
                  helperText={newNumberError || 'Ex: 5511999999999'}
                />
              </Box>
            </Grid>
          )}

          {/* Separador visual antes das seleções */}
          {(selectedContact || isCreating) && (
            <Grid xs={12} item>
              <Divider />
            </Grid>
          )}

          {/* Seleção de fila */}
          <Grid xs={12} item>
            <FormControl
              fullWidth
              variant="outlined"
              error={Boolean(queueError)}
              className={classes.field}
            >
              <Select
                required
                fullWidth
                displayEmpty
                value={selectedQueue}
                onChange={(e) => {
                  setSelectedQueue(e.target.value);
                  if (queueError) setQueueError('');
                }}
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedQueue === '') return 'Selecione uma fila';
                  return queues.find((q) => q.id === selectedQueue)?.name || 'Selecione uma fila';
                }}
              >
                {queues.map((queue, key) => (
                  <MenuItem dense key={key} value={queue.id}>
                    <ListItemText primary={queue.name} />
                  </MenuItem>
                ))}
              </Select>
              {queueError && <FormHelperText>{queueError}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Seleção de conexão */}
          <Grid xs={12} item>
            <Select
              required
              fullWidth
              displayEmpty
              variant="outlined"
              className={classes.field}
              value={selectedWhatsapp}
              onChange={(e) => setSelectedWhatsapp(e.target.value)}
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                getContentAnchorEl: null,
              }}
              renderValue={() => {
                if (selectedWhatsapp === '') return 'Selecione uma Conexão';
                return whatsapps.find((w) => w.id === selectedWhatsapp)?.name;
              }}
            >
              {whatsapps.map((whatsapp, key) => (
                <MenuItem dense key={key} value={whatsapp.id}>
                  <ListItemText
                    primary={
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {IconChannel(whatsapp.channel)}
                        <Typography style={{ fontSize: 14 }}>
                          {whatsapp.name}&nbsp;
                          <span
                            className={
                              whatsapp.status === 'CONNECTED' ? classes.online : classes.offline
                            }
                          >
                            ({whatsapp.status})
                          </span>
                        </Typography>
                      </Box>
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} className={classes.btn} disabled={loading} variant="outlined">
          {i18n.t('newTicketModal.buttons.cancel')}
        </Button>
        <ButtonWithSpinner
          startIcon={<CheckIcon />}
          variant="contained"
          color="primary"
          type="button"
          disabled={!canConfirm}
          onClick={handleSaveTicket}
          className={classes.btn}
          loading={loading}
          style={{
            fontSize: '12px',
            color: 'white',
            backgroundColor: '#437db5',
            boxShadow: 'none',
            borderRadius: 0,
          }}
        >
          {confirmLabel || i18n.t('newTicketModal.buttons.ok')}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default NewTicketModal;
